import { useQuery } from '@tanstack/react-query';
import useAxios from './use-axios';

/**
 * Response type for the attachments token API
 */
type AttachmentsTokenResponse = {
  token: string;
  expiresAt: number;
};

/**
 * Query key for attachments token
 */
export const ATTACHMENTS_TOKEN_QUERY_KEY = ['attachmentsToken'] as const;

/**
 * Token refresh interval: 2 minutes in milliseconds
 */
const TOKEN_REFRESH_INTERVAL = 2 * 60 * 1000;

/**
 * Hook for fetching and automatically refreshing the attachment download token.
 *
 * This hook is designed to be used while the Communication Hub is open. It will
 * automatically refresh the token every 2 minutes to ensure the user always has
 * a valid token for downloading attachments.
 *
 * @param enabled - Whether to enable the token refresh. Defaults to true.
 *                  Set to false when the Communication Hub is not open.
 *
 * @returns {object} An object containing:
 *   - {string | undefined} token - The attachment token
 *   - {number | undefined} expiresAt - Unix timestamp when the token expires
 *   - {boolean} isLoading - Whether the initial token fetch is loading
 *   - {boolean} isFetching - Whether a background refresh is in progress
 *   - {Error | null} error - Any error that occurred during fetching
 *   - {Function} refetch - Function to manually refetch the token
 */
export function useAttachmentsToken(enabled = true) {
  const axios = useAxios();

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery<AttachmentsTokenResponse>({
    queryKey: ATTACHMENTS_TOKEN_QUERY_KEY,
    queryFn: async () => {
      const response = await axios.get<AttachmentsTokenResponse>('/nylas/attachments/token');
      return response.data;
    },
    enabled,
    refetchInterval: TOKEN_REFRESH_INTERVAL,
    refetchIntervalInBackground: false, // Only refresh when tab is active
    staleTime: TOKEN_REFRESH_INTERVAL - 30_000, // Consider stale 30 seconds before refresh
    gcTime: TOKEN_REFRESH_INTERVAL * 2, // Keep in cache for 4 minutes
  });

  return {
    token: data?.token,
    expiresAt: data?.expiresAt,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}
